import React, { FC, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { MaterialGridInput } from 'models/order-input/shared';
import { formatCurrency } from 'utils/format';
import { CurrencyContext } from 'context/currency';

const computePriceWithoutVat = (material: MaterialGridInput): number => {
    const price = parseFloat(String(material.unitPrice || 0).replace(',', '.'));
    const quantity = parseFloat(String(material.quantity || '1').replace(',', '.')) || 1;
    return price * quantity;
};

const computePriceWithVat = (material: MaterialGridInput): number => {
    const priceWithoutVat = computePriceWithoutVat(material);
    const vatMultiplier = 1 + (parseInt(material.vatPercent || '0') / 100 || 0);
    return priceWithoutVat * vatMultiplier;
};

type ConstructionSummaryMaterialsProps = {
    materials: MaterialGridInput[];
};
const ConstructionSummaryMaterials: FC<ConstructionSummaryMaterialsProps> = ({ materials }) => {
    const { t: translate } = useTranslation(['actions-materials', 'orders']);
    const { currency } = useContext(CurrencyContext);

    const actionDescription = (material: MaterialGridInput): string => {
        let description = '';
        if (material.materialCode) {
            description += `${material.materialCode} - `;
        }
        if (material.description) {
            description += material.description;
        }
        return description;
    };

    return (
        <Table className={'dh-compact-table'}>
            <TableHead>
                <TableRow>
                    <TableCell>{translate('orders:MATERIAL')}</TableCell>
                    <TableCell sx={{ width: 150 }}>{translate('orders:BATCH_NUMBER')}</TableCell>
                    <TableCell sx={{ width: 80 }} align="right">
                        {translate('orders:QUANTITY')}
                    </TableCell>
                    <TableCell sx={{ width: 100 }} align="right">
                        {translate('orders:UNIT_PRICE')}
                    </TableCell>
                    <TableCell sx={{ width: 140 }} align="right">
                        {translate('orders:TOTAL_EXCL_VAT')}
                    </TableCell>
                    <TableCell sx={{ width: 90 }} align="right">
                        {translate('orders:VAT_PERCENT')}
                    </TableCell>
                    <TableCell sx={{ width: 140 }} align="right">
                        {translate('orders:TOTAL_INCL_VAT')}
                    </TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {materials?.map((material, index) => (
                    <TableRow key={index}>
                        <TableCell>{actionDescription(material)}</TableCell>
                        <TableCell>{material.batchNumber}</TableCell>
                        <TableCell align="right">{material.quantity}</TableCell>
                        <TableCell align="right">{formatCurrency(parseFloat(material.unitPrice), currency)}</TableCell>
                        <TableCell align="right">
                            {formatCurrency(computePriceWithoutVat(material), currency)}
                        </TableCell>
                        <TableCell align="right">{material.vatPercent}</TableCell>
                        <TableCell align="right">{formatCurrency(computePriceWithVat(material), currency)}</TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
};

export default ConstructionSummaryMaterials;
